'use client';

import type { BankAccount } from '@/services/PaymentService';
import type { InstallmentOption } from './PaymentCardForm';
import PaymentCardForm, { type PaymentCardFormProps, type PaymentCardFormValue } from './PaymentCardForm';
import PaymentBankTransferForm, { type PaymentBankTransferFormValue } from './PaymentBankTransferForm';

export type PaymentMethod = 'card' | 'bank';

export interface PaymentMethodSectionProps {
  /** Seçili ödeme yöntemi */
  paymentMethod: PaymentMethod;
  /** Ödeme yöntemi değişince */
  onPaymentMethodChange: (method: PaymentMethod) => void;
  /** Kart formu değeri */
  cardValue: PaymentCardFormValue;
  /** Kart formu değişince */
  onCardChange: (next: Partial<PaymentCardFormValue>) => void;
  /** Banka havalesi formu değeri */
  bankValue: PaymentBankTransferFormValue;
  /** Banka havalesi formu değişince */
  onBankChange: (next: Partial<PaymentBankTransferFormValue>) => void;
  /** Ödeme seçenekleri (test modu, provider, banka hesapları) */
  paymentOptions: {
    test_mode?: boolean;
    payment_provider_key?: string;
    bank_accounts?: BankAccount[];
  } | null;
  paymentOptionsLoading?: boolean;
  /** Çeviri */
  t: (key: string, fallback: string) => string;
  /** Dil (tr/en) – başlık ve açıklamalar için */
  language: string;
  /** Test kartı yükle (iyzico vb.) */
  onLoadTestCard?: () => void;
  /** InvoicePayment ile aynı: kart animasyonu (ön/arka) – varsayılan true */
  showCardAnimation?: boolean;
  /** InvoicePayment ile aynı: taksit seçenekleri */
  installments?: InstallmentOption[];
  installment?: number;
  onInstallmentChange?: (n: number) => void;
  installmentSectionTitle?: string;
  /** InvoicePayment ile aynı: para birimi sembolü (₺, $, vb.) */
  currencySymbol?: string;
  /** Form altı ipucu (opsiyonel) */
  cardHint?: string;
  /** Havale tutarı üst / alt sınır (kısmi ödeme) */
  bankMaxAmount?: number;
  bankMinAmount?: number;
  cardFieldErrors?: PaymentCardFormProps['fieldErrors'];
  /** Ödeme bu ekranda alınıyorsa (BookNow): banka havalesi bildirimi Confirm ile gönderilir, yönlendirme yok */
  bankPaymentOnThisScreen?: boolean;
  /** 'accordion' (varsayılan) veya 'tabs' – InvoicePayment ile aynı sekme görünümü için 'tabs' */
  variant?: 'accordion' | 'tabs';
}

/**
 * Rezervasyon/ödeme sayfalarında kullanılan ödeme yöntemi bölümü.
 * Kart ve banka havalesi için tek yerden PaymentCardForm + PaymentBankTransferForm kullanır.
 */
export default function PaymentMethodSection({
  paymentMethod,
  onPaymentMethodChange,
  cardValue,
  onCardChange,
  bankValue,
  onBankChange,
  paymentOptions,
  paymentOptionsLoading = false,
  t,
  language,
  onLoadTestCard,
  showCardAnimation = true,
  installments = [],
  installment = 1,
  onInstallmentChange,
  installmentSectionTitle,
  currencySymbol = '₺',
  cardHint,
  bankPaymentOnThisScreen = false,
  variant = 'accordion',
  bankMaxAmount,
  bankMinAmount,
  cardFieldErrors,
}: PaymentMethodSectionProps) {
  const sectionTitle = language === 'tr' ? 'Ödeme bilgilerini ekle' : 'Add payment details';
  const securityText =
    language === 'tr'
      ? 'Bu güvenli ve SSL ile şifrelenmiş bir ödemedir. Kredi kartı bilgileriniz güvendedir.'
      : 'This is a secure and SSL encrypted payment. Your credit card details are safe.';
  const selectMethodLabel = language === 'tr' ? 'Ödeme yönteminizi seçin:' : 'Select your payment method:';
  const bankAccounts = paymentOptions?.bank_accounts ?? [];
  const hasBankTransfer = bankAccounts.length > 0;

  const cardFormBlock = (
    <PaymentCardForm
      value={cardValue}
      onChange={onCardChange}
      t={t}
      testMode={!!paymentOptions?.test_mode}
      paymentProviderKey={paymentOptions?.payment_provider_key}
      onLoadTestCard={paymentOptions?.test_mode ? onLoadTestCard : undefined}
      showCardAnimation={showCardAnimation}
      installments={installments}
      installment={installment}
      onInstallmentChange={onInstallmentChange}
      installmentSectionTitle={installmentSectionTitle}
      currencySymbol={currencySymbol}
      cardHint={cardHint ?? t('payment.card_hint', 'Same fields as payment page: card holder, number, expiry, CVC, 3D Secure.')}
      fieldErrors={cardFieldErrors}
    />
  );

  const bankFormBlock = (
    <>
      <p className="text-sm text-gray-700">
        {bankPaymentOnThisScreen
          ? (language === 'tr'
              ? 'Rezervasyonu onayladığınızda havale bildiriminiz bu ekrandan gönderilecektir.'
              : 'When you confirm your booking, your transfer notification will be submitted on this screen.')
          : t(
              'payment.bank_transfer_hint',
              'After confirming your booking you will be redirected to the payment page to get bank details and submit your transfer notification.'
            )}
      </p>
      <PaymentBankTransferForm
        value={bankValue}
        onChange={onBankChange}
        bankAccounts={bankAccounts}
        t={t}
        maxAmount={bankMaxAmount}
        minAmount={bankMinAmount}
        prefillOnly
        prefillNote={
          bankPaymentOnThisScreen
            ? (language === 'tr'
                ? 'Aşağıdaki bilgileri doldurup "Rezervasyonu Onayla" ile havale bildiriminizi gönderin.'
                : 'Fill in the details below and submit your transfer notification with "Confirm Booking".')
            : language === 'tr'
              ? 'Rezervasyonu onayladıktan sonra ödeme sayfasına yönlendirileceksiniz; bu bilgileri orada havale bildirimi olarak gönderebilirsiniz.'
              : 'After confirming your booking you will be redirected to the payment page where you can submit this as your transfer notification.'
        }
      />
    </>
  );

  if (variant === 'tabs') {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="border-l-4 border-alsero-first bg-alsero-first/5 px-4 py-3">
          <h2 className="text-lg font-bold text-alsero-dark-gray">{sectionTitle}</h2>
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50/60 px-4 py-3 mb-6">
            <svg className="w-5 h-5 shrink-0 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-sky-700">{securityText}</p>
          </div>
          <div className="flex border-b border-gray-200 mb-6">
            <button
              type="button"
              onClick={() => onPaymentMethodChange('card')}
              className={`flex-1 px-6 py-4 text-center border-b-2 transition-colors ${
                paymentMethod === 'card'
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="text-base font-medium">{t('payment.card', 'Credit/Debit Card')}</span>
              </div>
              <p className="text-xs text-gray-500">{t('payment.secure', 'Secure with 3D Secure')}</p>
            </button>
            {hasBankTransfer && (
              <button
                type="button"
                onClick={() => onPaymentMethodChange('bank')}
                className={`flex-1 px-6 py-4 text-center border-b-2 transition-colors ${
                  paymentMethod === 'bank'
                    ? 'border-green-600 text-green-600 font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10.5 3L12 2l1.5 1M3 10l9-7 9 7" />
                  </svg>
                  <span className="text-base font-medium">{t('payment.bank_transfer', 'Bank Transfer')}</span>
                </div>
                <p className="text-xs text-gray-500">{t('payment.manual_approval', 'Manual approval required')}</p>
              </button>
            )}
          </div>
          <div>
            {paymentMethod === 'card' && <div className="space-y-4">{cardFormBlock}</div>}
            {paymentMethod === 'bank' && <div className="space-y-4">{bankFormBlock}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      <div className="border-l-4 border-alsero-first bg-alsero-first/5 px-4 py-3">
        <h2 className="text-lg font-bold text-alsero-dark-gray">{sectionTitle}</h2>
      </div>
      <div className="p-5 sm:p-6 space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50/60 px-4 py-3">
          <svg
            className="w-5 h-5 shrink-0 text-blue-600 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-sky-700">{securityText}</p>
        </div>
        <p className="text-sm font-medium text-gray-700">{selectMethodLabel}</p>

        {/* Kart accordion – PaymentCardForm bileşeni */}
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => onPaymentMethodChange('card')}
            className={`w-full flex items-center justify-between gap-3 px-4 py-4 text-left rounded-lg cursor-pointer transition-colors ${
              paymentMethod === 'card' ? 'bg-alsero-first/5 border border-alsero-first' : 'bg-white hover:bg-gray-50/80 border border-transparent'
            }`}
            aria-expanded={paymentMethod === 'card'}
          >
            <span className="font-semibold text-gray-900 flex items-center gap-3">
              <svg
                className="w-6 h-6 text-alsero-first shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              {t('payment.card', 'Credit/Debit Card')}
              <span className="flex items-center gap-1.5 text-gray-500 font-normal text-xs">
                <span className="rounded bg-gray-100 px-1.5 py-0.5">VISA</span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5">Mastercard</span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5">Amex</span>
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-gray-600">{t('payment.secure', 'Secure with 3D Secure')}</span>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${paymentMethod === 'card' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          {paymentMethod === 'card' && (
            <div className="px-4 pb-4 pt-2 space-y-4 bg-gray-50/50 border-t border-gray-100">
              {cardFormBlock}
            </div>
          )}
        </div>

        {/* Banka havalesi accordion – PaymentBankTransferForm bileşeni */}
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => onPaymentMethodChange('bank')}
            className={`w-full flex items-center justify-between gap-3 px-4 py-4 text-left transition-colors rounded-lg cursor-pointer ${
              paymentMethod === 'bank' ? 'bg-alsero-first/5 border border-alsero-first' : 'bg-white hover:bg-gray-50/80 border border-transparent'
            }`}
            aria-expanded={paymentMethod === 'bank'}
          >
            <span className="font-semibold text-gray-900 flex items-center gap-3">
              <svg
                className="w-6 h-6 text-gray-600 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10.5 3L12 2l1.5 1M3 10l9-7 9 7"
                />
              </svg>
              {t('payment.bank_transfer', 'Bank transfer')}
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${paymentMethod === 'bank' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {paymentMethod === 'bank' && (
            <div className="px-4 pb-4 pt-2 space-y-4 bg-gray-50/50 border-t border-gray-100">
              {bankFormBlock}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
